import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../../stores';
import { Endpoints, SystemSoundKind, DemocracyStatus, BusinessErrorCode } from '../../../../../../views/democracy/constants';
import { extractErrorMessage, extractErrorCode, buildSingleFileMultipart } from '../../../../../../views/democracy/utils';
import AudioSlot from './AudioSlot';

const KIND_LABELS = {
    [SystemSoundKind.NO_ANSWER]: 'Нет ответа',
    [SystemSoundKind.NEW_SANCTION]: 'Новая санкция',
    [SystemSoundKind.PRE_START_TIMER]: 'Обратный отсчёт'
};
const GUARD = 'Доступно только в статусе «Ожидание» (перед стартом игры)';

// Секция системных звуков игры (в модалке настроек). Только в WAITING_ACTIVE.
export default function SystemSoundsSection({ democracy, sounds, onChanged }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const canEdit = democracy?.status === DemocracyStatus.WAITING_ACTIVE;

    const urlByKind = {};
    (sounds || []).forEach(s => { urlByKind[s.soundKind] = s.media?.mediaFileUrl || null; });

    const onGuardError = (error, fallback) => {
        const code = extractErrorCode(error);
        if (code === BusinessErrorCode.INVALID_DEMOCRACY_STATUS || error?.response?.status === 409) {
            enqueueSnackbar(GUARD, { variant: 'warning' });
        } else {
            enqueueSnackbar(extractErrorMessage(error, fallback), { variant: 'error' });
        }
    };

    const handleUpload = async (kind, file) => {
        show();
        try {
            await apiClient.put(Endpoints.democracies.systemSoundsKind(democracy.id, kind), buildSingleFileMultipart(file));
            enqueueSnackbar('Звук сохранён', { variant: 'success' });
            onChanged();
        } catch (error) {
            onGuardError(error, 'Не удалось сохранить звук');
        } finally {
            hide();
        }
    };

    const handleDelete = async (kind) => {
        show();
        try {
            await apiClient.delete(Endpoints.democracies.systemSoundsKind(democracy.id, kind));
            enqueueSnackbar('Звук сброшен', { variant: 'success' });
            onChanged();
        } catch (error) {
            onGuardError(error, 'Не удалось сбросить звук');
        } finally {
            hide();
        }
    };

    return (
        <div>
            <div className="sec-title">
                <span className="kicker">Звук</span>
                <div className="ttl">Системные звуки</div>
            </div>
            <div className="media-row">
                {Object.values(SystemSoundKind).map(kind => (
                    <AudioSlot
                        key={kind}
                        caption={KIND_LABELS[kind]}
                        url={urlByKind[kind] || null}
                        disabled={!canEdit}
                        onUpload={(f) => handleUpload(kind, f)}
                        onClear={() => handleDelete(kind)}
                    />
                ))}
            </div>
        </div>
    );
}

SystemSoundsSection.propTypes = {
    democracy: PropTypes.object.isRequired,
    sounds: PropTypes.array,
    onChanged: PropTypes.func.isRequired
};

SystemSoundsSection.defaultProps = { sounds: [] };
